import * as React from 'react';
import { useMemo, useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Column, Pie } from '@ant-design/plots';
import { Spin, Tabs } from 'antd';

export const CHART_CARD_MIN_HEIGHT = 320;
export const CHART_HEIGHT = 280;

export const CHART_CARD_STYLE: React.CSSProperties = {
    background: '#fff',
    borderRadius: 8,
    padding: 16,
    border: '1px solid #f0f0f0',
    minHeight: CHART_CARD_MIN_HEIGHT,
};

const TITLE_STYLE: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 14,
    color: '#222',
    marginBottom: 12,
};

const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];
const AGE_LABELS: Record<string, string> = { '13-17': '17-' };

const GENDER_COLORS: Record<string, string> = {
    female: '#7c5cfc',
    male: '#f5c518',
    unknown: '#d9d9d9',
};

function formatPct(pct: number): string {
    if (pct > 0 && pct < 1) return '<1%';
    return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

function sortedBreakdown(breakdown: API.Creators.DemographicsBreakdownItem[]) {
    return [...breakdown].sort((a, b) => b.percentage - a.percentage);
}

function RankedListBody({
    items,
    pageSize = 5,
}: {
    items: API.Creators.DemographicsBreakdownItem[];
    pageSize?: number;
}) {
    const [page, setPage] = useState(0);
    const sorted = useMemo(() => sortedBreakdown(items), [items]);
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const slice = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);
    const showPager = sorted.length > pageSize;

    return (
        <>
            {showPager && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: 8 }}>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={safePage === 0}
                        style={{ border: 'none', background: 'transparent', cursor: safePage === 0 ? 'default' : 'pointer', color: safePage === 0 ? '#d9d9d9' : '#595959' }}
                        aria-label="Previous page"
                    >
                        <LeftOutlined />
                    </button>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={safePage >= totalPages - 1}
                        style={{ border: 'none', background: 'transparent', cursor: safePage >= totalPages - 1 ? 'default' : 'pointer', color: safePage >= totalPages - 1 ? '#d9d9d9' : '#595959' }}
                        aria-label="Next page"
                    >
                        <RightOutlined />
                    </button>
                </div>
            )}
            <div>
                {slice.map((item, i) => (
                    <div
                        key={item.key}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 0',
                            borderBottom: i < slice.length - 1 ? '1px solid #f5f5f5' : undefined,
                        }}
                    >
                        <span style={{ color: '#333', fontSize: 14 }}>{item.label || item.key}</span>
                        <span style={{ color: '#333', fontSize: 14, fontWeight: 500 }}>{formatPct(item.percentage)}</span>
                    </div>
                ))}
            </div>
        </>
    );
}

function LanguageCountryCard({
    language,
    country,
    chartHeight = CHART_HEIGHT,
}: {
    language: API.Creators.DemographicsBreakdownItem[];
    country: API.Creators.DemographicsBreakdownItem[];
    chartHeight?: number;
}) {
    const [activeTab, setActiveTab] = useState<'language' | 'country'>('language');

    return (
        <div style={CHART_CARD_STYLE}>
            <Tabs
                activeKey={activeTab}
                onChange={(k) => setActiveTab(k as 'language' | 'country')}
                size="small"
                items={[
                    {
                        key: 'language',
                        label: 'Language',
                        children: (
                            <div style={{ minHeight: chartHeight ? chartHeight - 72 : undefined }}>
                                <RankedListBody items={language} />
                            </div>
                        ),
                    },
                    {
                        key: 'country',
                        label: 'Country',
                        children: (
                            <div style={{ minHeight: chartHeight ? chartHeight - 72 : undefined }}>
                                <RankedListBody items={country} pageSize={10} />
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
}

function AgeChart({
    breakdown,
    chartHeight = CHART_HEIGHT,
}: {
    breakdown: API.Creators.DemographicsBreakdownItem[];
    chartHeight?: number;
}) {
    const byKey = Object.fromEntries(breakdown.map((b) => [b.key, b]));
    const countByAge = Object.fromEntries(
        AGE_ORDER.filter((k) => byKey[k]).map((k) => [AGE_LABELS[k] ?? k, byKey[k].count]),
    );
    const data = AGE_ORDER.filter((k) => byKey[k]).map((k) => ({
        age: AGE_LABELS[k] ?? k,
        value: byKey[k].percentage,
    }));

    return (
        <div style={CHART_CARD_STYLE}>
            <div style={TITLE_STYLE}>Age</div>
            <Column
                data={data}
                xField="age"
                yField="value"
                color="#69b1ff"
                height={chartHeight}
                autoFit
                yAxis={{
                    label: {
                        formatter: (v: string) => `${v}%`,
                    },
                }}
                tooltip={{
                    formatter: (d: { age?: string; value?: number }) => {
                        const age = d.age ?? '';
                        const value = d.value ?? 0;
                        const count = countByAge[age] ?? 0;
                        return {
                            name: 'Share',
                            value: `${formatPct(value)} (${count.toLocaleString()})`,
                        };
                    },
                }}
                meta={{ value: { alias: 'Percentage' } }}
            />
        </div>
    );
}

function GenderChart({
    breakdown,
    chartHeight = CHART_HEIGHT,
}: {
    breakdown: API.Creators.DemographicsBreakdownItem[];
    chartHeight?: number;
}) {
    const chartItems = breakdown.filter((b) => b.key === 'female' || b.key === 'male');
    const dominant = chartItems.reduce(
        (best, item) => (item.percentage > best.percentage ? item : best),
        chartItems[0] ?? { label: '—', percentage: 0 },
    );

    const data = chartItems.map((b) => ({
        type: b.label || b.key,
        value: b.percentage,
        color: GENDER_COLORS[b.key] ?? '#d9d9d9',
    }));

    return (
        <div style={CHART_CARD_STYLE}>
            <div style={TITLE_STYLE}>Gender</div>
            <Pie
                data={data}
                angleField="value"
                colorField="type"
                color={data.map((d) => d.color)}
                radius={0.9}
                innerRadius={0.62}
                height={chartHeight}
                autoFit
                legend={{
                    position: 'bottom' as const,
                    itemName: {
                        formatter: (_: string, item: { value: string }) => {
                            const row = data.find((d) => d.type === item.value);
                            return `${item.value}  ${formatPct(row?.value ?? 0)}`;
                        },
                    },
                }}
                statistic={{
                    title: false as const,
                    content: {
                        style: { fontSize: '18px', fontWeight: 600, color: '#222' },
                        content: `${formatPct(dominant.percentage)}\n${dominant.label || ''}`,
                    },
                }}
                tooltip={{
                    formatter: (d: { type?: string; value?: number }) => ({
                        name: d.type ?? '',
                        value: formatPct(d.value ?? 0),
                    }),
                }}
            />
        </div>
    );
}

export function hasUsableDemographics(data: API.Creators.AudienceDemographics | null): boolean {
    if (!data) return false;
    const dims = [data.gender, data.age, data.country, data.language];
    return dims.every(
        (d) =>
            d &&
            d.confidence !== 'insufficient' &&
            Array.isArray(d.breakdown) &&
            d.breakdown.length > 0,
    );
}

const DemographicsPanel: React.FC<{
    data: API.Creators.AudienceDemographics | null;
    loading: boolean;
    chartHeight?: number;
    embedded?: boolean;
}> = ({ data, loading, chartHeight = CHART_HEIGHT, embedded = false }) => {
    if (loading) {
        if (embedded) {
            return (
                <div style={{ ...CHART_CARD_STYLE, gridColumn: '1 / -1', textAlign: 'center' }}>
                    <Spin />
                </div>
            );
        }
        return (
            <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
            </div>
        );
    }

    if (!hasUsableDemographics(data)) {
        if (embedded) {
            return (
                <div style={{ ...CHART_CARD_STYLE, gridColumn: '1 / -1', textAlign: 'center', color: '#bbb' }}>
                    No audience demographics yet.
                </div>
            );
        }
        return (
            <div style={{ textAlign: 'center', color: '#bbb', padding: '24px 0' }}>
                No audience demographics yet.
            </div>
        );
    }

    const demographics = data!;
    const widgets = (
        <>
            <AgeChart breakdown={demographics.age.breakdown} chartHeight={chartHeight} />
            <GenderChart breakdown={demographics.gender.breakdown} chartHeight={chartHeight} />
            <LanguageCountryCard
                language={demographics.language.breakdown}
                country={demographics.country.breakdown}
                chartHeight={chartHeight}
            />
        </>
    );

    if (embedded) {
        return widgets;
    }

    return (
        <div style={{ marginTop: 24 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 12 }}>
                Audience Demographics
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 16,
                }}
            >
                {widgets}
            </div>
        </div>
    );
};

export default DemographicsPanel;
